import React from 'react';
import { translate } from '@utils/i18n';
import IssueCard from '../IssueCard';
import { IssueTypeEnum, ModeIA } from '../../types';
import { humanizeInspectableType, isKnownAssetType } from '../../utils/humanizeInspectableType';
const VISUAL_CHECK_TYPES = new Set([
    IssueTypeEnum.AnalyzeDeficiencyPhotos,
    IssueTypeEnum.SignatureQualityCheck,
    IssueTypeEnum.PumpCurveCheck,
    IssueTypeEnum.DuplicatePhotoDetected
]);
const NS = 'features.iim_review.issue_detail_blade';
/**
 * Renders the full type-specific issue content inside the blade body.
 *
 * Rather than duplicating the rendering logic from each of the 14 issue card
 * types, we embed the existing IssueCard component and use CSS
 * (`.issue-detail-blade__card-embed`) to strip the accordion chrome
 * (header, action buttons, status tags) that the blade's own header/footer
 * already provide.  This guarantees the blade always shows the same rich
 * content — grammar diffs, occurrence-history tables, out-of-spec ranges,
 * proximity violation grids, etc. — that the card view shows.
 */
export default function IssueDetailBladeContent({ issue, inspectionId, modeIA }) {
    var _a, _b, _c;
    const readOnly = modeIA === ModeIA.ReadOnly || modeIA === ModeIA.Trial || modeIA === ModeIA.Teaser;
    const isVisualCheck = VISUAL_CHECK_TYPES.has(issue.issueType);
    const inspectableData = ((_a = issue.inspectable) === null || _a === void 0 ? void 0 : _a.data) || {};
    const showAsset = !isVisualCheck && isKnownAssetType((_b = issue.inspectable) === null || _b === void 0 ? void 0 : _b.type);
    const assetType = showAsset ? humanizeInspectableType(issue.inspectable.type) : null;
    const assetName = showAsset
        ? inspectableData.name ||
            inspectableData.deviceName ||
            inspectableData.device_name ||
            null
        : null;
    const deficiencyData = (((_c = issue.deficiency) === null || _c === void 0 ? void 0 : _c.data) || {});
    const deficiencyNotes = !isVisualCheck ? deficiencyData.notes || null : null;
    return (React.createElement(React.Fragment, null,
        (assetType || assetName) && (React.createElement("div", { className: "issue-detail-blade__context-section" },
            React.createElement("h4", { className: "issue-detail-blade__section-label" }, translate('asset_section', { namespace: NS, defaultValue: 'Asset' })),
            React.createElement("dl", { className: "issue-detail-blade__detail-list" },
                assetType && (React.createElement(React.Fragment, null,
                    React.createElement("dt", null, translate('asset_type', { namespace: NS, defaultValue: 'Type' })),
                    React.createElement("dd", null, assetType))),
                assetName && (React.createElement(React.Fragment, null,
                    React.createElement("dt", null, translate('asset_name', { namespace: NS, defaultValue: 'Name' })),
                    React.createElement("dd", null, assetName)))))),
        issue.deficiency && deficiencyNotes && (React.createElement("div", { className: "issue-detail-blade__context-section" },
            React.createElement("h4", { className: "issue-detail-blade__section-label" }, translate('deficiency_section', { namespace: NS, defaultValue: 'Deficiency' })),
            React.createElement("p", { className: "issue-detail-blade__deficiency-notes" }, deficiencyNotes))),
        React.createElement("div", { className: "issue-detail-blade__card-embed" },
            React.createElement(IssueCard, { issue: issue, inspectionId: inspectionId, readOnly: readOnly }))));
}
